import { useState, useCallback, useEffect, useMemo } from "react";
import { useTenant } from "../../../hooks/useTenant";
import { useMembers } from "../../../hooks/useMembers";
import { toast } from "react-toastify";
import MemberService from "../../../services/member_service";
import ActivityService from "../../../services/activity_service";
import { initialMemberValues } from "../../../models/member";

/**
 * useMembersPage
 * Hook orquestrador para a página de Clientes (Members).
 * Consome membros do MembersProvider (cache centralizado) em vez de
 * chamar MemberService diretamente, economizando ~682 reads por abertura.
 */
export const useMembersPage = () => {
    const { idTenant, user, loading: tenantLoading } = useTenant();
    const { members: allMembers, loading: membersLoading, refresh: refreshMembers, removeMember } = useMembers();

    // Estados de Dados
    const [activities, setActivities] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Estados do Formulário/Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formState, setFormState] = useState(initialMemberValues);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    /**
     * Carrega atividades (membros vêm do MembersProvider)
     */
    const loadActivities = useCallback(async () => {
        if (!idTenant) return;
        try {
            const activitiesData = await ActivityService.getActivities(idTenant);
            setActivities(activitiesData);
        } catch (error) {
            console.error("Erro ao carregar atividades:", error);
        }
    }, [idTenant]);

    useEffect(() => {
        if (!tenantLoading && idTenant) {
            loadActivities();
        }
    }, [tenantLoading, idTenant, loadActivities]);

    // Sincronizar loading com o MembersProvider
    useEffect(() => {
        setIsInitialLoading(membersLoading);
    }, [membersLoading]);

    /**
     * Filtro local apneas para a busca de texto
     */
    const filteredMembers = useMemo(() => {
        // Filtro de status (local — o cache tem todos)
        let result = allMembers;
        if (statusFilter !== "all") {
            result = result.filter(m => m.status === statusFilter);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const termNorm = term.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            result = result.filter(m => {
                const displayName = m.fullName || m.name || "";
                const nameNorm = displayName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                // searchName \u00e9 pr\u00e9-normalizado pelo converter (sem acentos, lowercase)
                const preNorm = m.searchName || "";
                return preNorm.includes(termNorm) ||
                    nameNorm.includes(termNorm) ||
                    displayName.toLowerCase().includes(term) ||
                    (m.email || "").toLowerCase().includes(term) ||
                    (m.memberNumber || "").includes(searchTerm.trim());
            });
        }
        // Ordenar alfabeticamente pelo nome
        result = [...result].sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'pt-BR'));

        return result;
    }, [allMembers, searchTerm, statusFilter]);

    /**
     * Handlers do Modal
     */
    const handleToggleModal = useCallback(() => {
        if (isModalOpen) {
            setFormState(initialMemberValues);
        }
        setIsModalOpen(prev => !prev);
    }, [isModalOpen]);

    const handleEdit = useCallback((member) => {
        setFormState(member);
        setIsModalOpen(true);
    }, []);

    const handleSave = useCallback(async (data) => {
        setIsSaving(true);
        try {
            await MemberService.save(data, idTenant, user, allMembers);
            toast.success(data.id ? "Cliente atualizado!" : "Cliente cadastrado!");
            await refreshMembers(); // Recarrega cache centralizado
            handleToggleModal();
        } catch (error) {
            console.error("Erro ao salvar membro:", error);
            toast.error("Erro ao salvar cliente.");
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, allMembers, refreshMembers, handleToggleModal]);

    const handleDelete = useCallback(async (idMember, memberName) => {
        if (!window.confirm(`Tem certeza que deseja excluir ${memberName}?`)) return;

        try {
            // Remove localmente para feedback visual instantâneo
            removeMember(idMember);
            
            await MemberService.delete(idMember, idTenant, user, memberName);
            toast.success("Cliente removido!");
            refreshMembers(); // Recarrega cache centralizado em background para manter 100% em sincronia
        } catch (error) {
            console.error("Erro ao excluir membro:", error);
            toast.error("Erro ao excluir cliente.");
            refreshMembers(); // Reverte o estado em caso de falha
        }
    }, [idTenant, user, removeMember, refreshMembers]);

    return {
        // Dados
        members: filteredMembers,
        activities,
        isInitialLoading,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,

        // Modal/Form
        isModalOpen,
        setIsModalOpen,
        handleToggleModal,
        formState,
        setFormState,
        isSaving,
        handleSave,
        handleEdit,
        handleDelete,

        // Status
        isReady: !tenantLoading && !!idTenant
    };
};
